import { notification } from "antd";
import { get, isEmpty } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { persistProcessRuntime } from "screens/Private/Broker/Projects/Detail/resolveProcessRuntime";
import { DOMAIN_SIGN_IN } from "utils/api/graphql/mutations/users";
import { LINK } from "utils/api/graphql/queries/link";
import { PROJECT } from "utils/api/graphql/queries/projects";
import { TRACER } from "utils/api/graphql/queries/tracers";
import { KEYS, SCREENS } from "utils/constants";
import {
  buildDomainSignInData,
  resolveDomainSignInUserId,
} from "./domainSignInUser";
import { readProcessInitParams, toQueryString } from "./processInitParams";

const isValidParam = (value) => value && value !== "undefined";

const Init = () => {
  const navigate = useNavigate();
  const [signIn] = useMutation(DOMAIN_SIGN_IN, { fetchPolicy: "no-cache" });
  const { src } = useParams();
  const [host, setHost] = useState(null);
  const [getTracer] = useLazyQuery(TRACER);
  const [getProject] = useLazyQuery(PROJECT);
  const [getLink] = useLazyQuery(LINK);

  const { queryParams, queryString, url } = useMemo(() => {
    const url = window.location.href;
    const queryParams = readProcessInitParams(url);
    return { queryParams, queryString: toQueryString(queryParams), url };
  }, []);

  const navigateToProject = useCallback(
    (tracer, projectId, subscriptionId, statusId) => {
      const cleanTracer = omitDeep(tracer, "__typename");
      const process = get(cleanTracer, "flow.process");
      persistProcessRuntime(projectId, process, cleanTracer);
      navigate(
        `${SCREENS.PRIVATE.GUEST.PROJECTS.DETAIL.path}/${projectId}${queryString}`,
        {
          state: {
            process,
            tracer: cleanTracer,
            subscriptionId: isValidParam(subscriptionId)
              ? subscriptionId
              : undefined,
            status: get(process, "processStatuses", []).find(
              ({ id }) => id === statusId,
            ),
          },
        },
      );
    },
    [navigate, queryString],
  );

  const navigateToCreate = useCallback(
    (user) => {
      localStorage.setItem(KEYS.TEMPLATE_HISTORY, "{}");
      navigate(`${SCREENS.PUBLIC.PROCESSES.CREATE.path}/${src}${queryString}`, {
        state: {
          user,
        },
      });
    },
    [navigate, src, queryString],
  );

  const handleProjectNavigation = useCallback(
    (user) => {
      const projectId = localStorage.getItem(KEYS.PROJECT_ID);
      const subscriptionId = localStorage.getItem(KEYS.SUBSCRIPTION_ID);
      const statusId = localStorage.getItem(KEYS.STATUS_ID);

      localStorage.removeItem(KEYS.USER);

      if (isValidParam(projectId)) {
        getProject({
          fetchPolicy: "no-cache",
          variables: { where: { id: projectId } },
          onCompleted: ({ project }) => {
            if (isEmpty(project)) {
              return navigateToCreate(user);
            }
            getTracer({
              variables: { where: { source: { id: src } } },
              onCompleted: ({ tracer }) => {
                navigateToProject(tracer, projectId, subscriptionId, statusId);
              },
              onError: () => navigateToCreate(user),
            });
          },
          onError: () => navigateToCreate(user),
        });
      } else {
        navigateToCreate(user);
      }
    },
    [src, getProject, getTracer, navigateToProject, navigateToCreate],
  );

  useEffect(() => {
    const currentSource = localStorage.getItem(KEYS.SOURCE);
    if (currentSource && currentSource !== src) {
      localStorage.clear();
    }
    localStorage.setItem(KEYS.SOURCE, src);

    if (window.self === window.top) {
      const projectIdParams = queryParams.get("p");
      const statusIdParams = queryParams.get("status");
      const linkIdParams = queryParams.get("l");
      const cuidParams = queryParams.get("cuid");
      const trackonReqidParams = queryParams.get("id");
      if (isValidParam(projectIdParams)) {
        localStorage.setItem(KEYS.PROJECT_ID, projectIdParams);
      }
      if (isValidParam(statusIdParams)) {
        localStorage.setItem(KEYS.STATUS_ID, statusIdParams);
      }
      if (isValidParam(linkIdParams)) {
        localStorage.setItem(KEYS.LINK_ID, linkIdParams);
      }
      if (isValidParam(trackonReqidParams)) {
        localStorage.setItem(KEYS.TRACKON_REQID, trackonReqidParams);
      } else {
        localStorage.removeItem(KEYS.TRACKON_REQID);
      }

      const [cleanedBaseUrl] = url.split("?");
      if (cuidParams) return setHost(`${cleanedBaseUrl}?cuid=${cuidParams}`);
      setHost(cleanedBaseUrl);
      return;
    }

    const handleMessage = (event) => {
      if (
        !process.env.REACT_APP_PROCESS_FLOWS.split(",").includes(event.origin)
      ) {
        return;
      }

      const { url: parentUrl } = event.data;
      const parsedUrl = new URL(parentUrl);
      const params = new URLSearchParams(parsedUrl.search);

      const paramMap = {
        p: KEYS.PROJECT_ID,
        l: KEYS.LINK_ID,
        s: KEYS.SUBSCRIPTION_ID,
        status: KEYS.STATUS_ID,
        id: KEYS.TRACKON_REQID,
      };

      Object.entries(paramMap).forEach(([key, storageKey]) => {
        const value = params.get(key);
        if (isValidParam(value)) {
          localStorage.setItem(storageKey, value);
        }
      });

      setHost(parsedUrl.host);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [src, url, queryParams]);

  useEffect(() => {
    if (!host) return;
    const userId = resolveDomainSignInUserId(
      queryParams.get("uid"),
      localStorage.getItem(KEYS.GUEST_USER_ID),
    );
    signIn({
      variables: { data: buildDomainSignInData(host, userId) },
      onCompleted: ({ domainSignIn }) => {
        localStorage.setItem(KEYS.ACCESS_TOKEN, domainSignIn?.accessToken);
        if (userId) {
          localStorage.setItem(KEYS.GUEST_USER_ID, userId);
        } else {
          localStorage.removeItem(KEYS.GUEST_USER_ID);
        }

        const linkIdParams = queryParams.get("l");
        const link = linkIdParams || localStorage.getItem(KEYS.LINK_ID);

        const signedInUser = get(domainSignIn, "user");
        if (isValidParam(link)) {
          localStorage.setItem(KEYS.LINK_ID, link);
          getLink({
            fetchPolicy: "no-cache",
            variables: { where: { id: link } },
            onCompleted: ({ link }) => {
              const projectId = get(link, "project.id");
              const subscriptionId = get(link, "subscription.id");
              const statusId = get(link, "fields.status.id");

              if (projectId) localStorage.setItem(KEYS.PROJECT_ID, projectId);
              if (subscriptionId)
                localStorage.setItem(KEYS.SUBSCRIPTION_ID, subscriptionId);
              if (statusId) localStorage.setItem(KEYS.STATUS_ID, statusId);

              handleProjectNavigation(signedInUser);
            },
            onError: () => {
              handleProjectNavigation(signedInUser);
            },
          });
          return;
        }

        handleProjectNavigation(signedInUser);
      },
      onError: (error) => {
        notification.error({
          message: "Error signing in",
          description: error.message,
        });
      },
    });
  }, [host, signIn, queryParams, getLink, handleProjectNavigation]);

  return <Loading />;
};

export default Init;
